import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { accessoryOrdersApi, AccessoryOrder, ProvisionedDevice } from '../api/accessoryShop';
import { bridgeClaimApi } from '../api/signShopOrders';
import QRCode from 'qrcode';

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const statusMeta: Record<AccessoryOrder['status'], { label: string; color: string }> = {
  pending_payment:        { label: 'Awaiting payment',  color: 'yellow' },
  paid_pending_approval:  { label: 'Paid — needs approval', color: 'blue' },
  approved:               { label: 'Approved — ready to ship', color: 'purple' },
  shipped:                { label: 'Shipped', color: 'green' },
  rejected:               { label: 'Rejected', color: 'gray' },
};

const STATUS_FILTERS: Array<{ key: string; label: string }> = [
  { key: 'all',                    label: 'All' },
  { key: 'paid_pending_approval',  label: 'Needs approval' },
  { key: 'approved',               label: 'Ready to ship' },
  { key: 'shipped',                label: 'Shipped' },
  { key: 'rejected',               label: 'Rejected' },
  { key: 'pending_payment',        label: 'Unpaid' },
];

export const AccessoryShopOrders: React.FC = () => {
  const [orders, setOrders] = useState<AccessoryOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('paid_pending_approval');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<AccessoryOrder | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [shipping, setShipping] = useState<AccessoryOrder | null>(null);
  const [trackingNum, setTrackingNum] = useState('');
  const [carrier, setCarrier] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [kitDevice, setKitDevice] = useState<ProvisionedDevice | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try { setOrders(await accessoryOrdersApi.listOrders()); }
    catch (e: any) { setError(e?.message || 'Failed to load orders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const approve = async (o: AccessoryOrder) => {
    if (!window.confirm(`Approve order ${o.id.slice(0, 8)}… for fulfillment?`)) return;
    setPendingId(o.id);
    try {
      const u = await accessoryOrdersApi.approve(o.id);
      setOrders(p => p.map(x => x.id === o.id ? u : x));
    } catch (e: any) { setError(`Approve failed: ${e?.message || e}`); }
    finally { setPendingId(null); }
  };

  const reject = async () => {
    if (!rejecting) return;
    setPendingId(rejecting.id);
    try {
      const u = await accessoryOrdersApi.reject(rejecting.id, rejectReason);
      setOrders(p => p.map(x => x.id === rejecting.id ? u : x));
      setRejecting(null); setRejectReason('');
    } catch (e: any) { setError(`Reject failed: ${e?.message || e}`); }
    finally { setPendingId(null); }
  };

  const ship = async () => {
    if (!shipping || !trackingNum.trim()) return;
    setPendingId(shipping.id);
    try {
      const u = await accessoryOrdersApi.ship(shipping.id, trackingNum.trim(), carrier.trim());
      setOrders(p => p.map(x => x.id === shipping.id ? u : x));
      setShipping(null); setTrackingNum(''); setCarrier('');
    } catch (e: any) { setError(`Ship failed: ${e?.message || e}`); }
    finally { setPendingId(null); }
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto mb-6 flex items-center justify-between gap-3 flex-wrap">
    <div className="shadow-page-frame legacy-order-page">
          <h1 className="text-3xl font-bold text-gray-900">Accessory Shop Orders</h1>
          <p className="text-gray-500 mt-1">Approve paid orders and add tracking once shipped.</p>
        </div>
        <Button onClick={load} variant="secondary" size="sm" loading={loading}>Refresh</Button>
      </div>

      <div className="max-w-7xl mx-auto mb-4 flex flex-wrap gap-2">
        {STATUS_FILTERS.map(f => {
          const count = f.key === 'all' ? orders.length : orders.filter(o => o.status === f.key).length;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                filter === f.key
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
              }`}
            >
              {f.label}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {error && <div className="max-w-7xl mx-auto mb-4"><Card><div className="text-red-700 text-sm">{error}</div></Card></div>}

      <div className="max-w-7xl mx-auto space-y-3">
        {loading && <Card><div className="text-gray-500 text-center py-4">Loading…</div></Card>}
        {!loading && filtered.length === 0 && <Card><div className="text-gray-500 text-center py-4">No orders in this view.</div></Card>}
        {!loading && filtered.map(o => {
          const meta = statusMeta[o.status] || { label: o.status, color: 'gray' };
          const isOpen = expanded.has(o.id);
          return (
            <Card key={o.id} padding={false} className="overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge color={meta.color}>{meta.label}</Badge>
                      <code className="text-xs text-gray-500">{o.id.slice(0, 8)}…</code>
                      <span className="text-xs text-gray-400">{new Date(o.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 font-medium text-gray-900">
                      {o.customer_email} — {o.cart.length} item{o.cart.length === 1 ? '' : 's'} — {fmt(o.total_cents)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {o.address.first_name} {o.address.last_name}, {o.address.address1}, {o.address.city} {o.address.region} {o.address.zip}
                    </div>
                    {o.tracking_number && (
                      <div className="text-sm text-emerald-700 mt-1">
                        📦 {o.carrier || 'Carrier'}: <code>{o.tracking_number}</code>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => toggleExpand(o.id)} size="sm" variant="ghost">
                      {isOpen ? 'Hide' : 'Details'}
                    </Button>
                    {o.status === 'paid_pending_approval' && (
                      <>
                        <Button onClick={() => approve(o)} size="sm" loading={pendingId === o.id}>Approve</Button>
                        <Button onClick={() => setRejecting(o)} size="sm" variant="secondary">Reject</Button>
                      </>
                    )}
                    {o.status === 'approved' && (
                      <Button onClick={() => setShipping(o)} size="sm">Mark shipped</Button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 border-t pt-4 space-y-4">
                    {(o.provisioned_devices || []).length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-gray-600 uppercase mb-2">
                          Provisioned bridges ({o.provisioned_devices!.length})
                        </div>
                        <div className="space-y-2">
                          {o.provisioned_devices!.map(d => (
                            <div key={d.serial} className="border border-emerald-200 bg-emerald-50/40 rounded p-3 text-sm">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <code className="font-mono text-gray-900">{d.serial}</code>
                                <Badge color="gray">{d.role}</Badge>
                                {d.has_cellular && <Badge color="blue">cellular</Badge>}
                                <span className="text-xs text-gray-500">{d.device_profile_name}</span>
                                <Button size="sm" variant="secondary" onClick={() => setKitDevice(d)}>
                                  Provisioning kit
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                <div>
                                  <div className="text-gray-500 mb-0.5">Customer activation code</div>
                                  <div className="flex items-center gap-1">
                                    <code className="font-mono text-lg text-emerald-900 bg-white px-2 py-1 rounded border border-emerald-200">{d.activation_code}</code>
                                    <button onClick={() => navigator.clipboard.writeText(d.activation_code)} className="text-emerald-700 hover:text-emerald-900 text-xs underline">copy</button>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-500 mb-0.5">Install token (one-time, Pi pulls cert)</div>
                                  <code className="font-mono text-xs text-gray-700 break-all">{d.install_token}</code>
                                </div>
                                <div>
                                  <div className="text-gray-500 mb-0.5">IoT thing</div>
                                  <code className="text-xs text-gray-700">{d.iot_thing_name}</code>
                                </div>
                                <div>
                                  <div className="text-gray-500 mb-0.5">SSH private key (operator)</div>
                                  <code className="text-xs text-gray-700 break-all">{d.ssh_private_key_secret_arn}</code>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Cart</div>
                      <div className="divide-y border rounded">
                        {o.cart.map((l, i) => (
                          <div key={i} className="p-2 flex items-center gap-2 text-sm">
                            {l.imageSrc && <img src={l.imageSrc} alt="" className="w-10 h-10 object-cover rounded" />}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{l.productTitle}</div>
                              <div className="text-gray-500 text-xs">× {l.quantity}</div>
                            </div>
                            <div className="text-gray-900 font-medium">{fmt(l.unitPrice * l.quantity)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-sm space-y-1">
                        <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmt(o.subtotal_cents)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{fmt(o.shipping_cents)}</span></div>
                        <div className="flex justify-between font-semibold"><span>Total</span><span>{fmt(o.total_cents)}</span></div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-2">References</div>
                      <dl className="text-sm space-y-1">
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Stripe session</dt><dd className="text-gray-800 truncate">{o.stripe_session_id || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Payment intent</dt><dd className="text-gray-800 truncate">{o.stripe_payment_intent_id || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Paid at</dt><dd className="text-gray-800">{o.paid_at ? new Date(o.paid_at).toLocaleString() : '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Approved at</dt><dd className="text-gray-800">{o.approved_at ? new Date(o.approved_at).toLocaleString() : '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Shipped at</dt><dd className="text-gray-800">{o.shipped_at ? new Date(o.shipped_at).toLocaleString() : '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Carrier</dt><dd className="text-gray-800">{o.carrier || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Tracking #</dt><dd className="text-gray-800">{o.tracking_number || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Phone</dt><dd className="text-gray-800">{o.address.phone || '—'}</dd></div>
                        {o.rejection_reason && (
                          <div className="flex gap-2"><dt className="text-gray-500 w-32">Reject reason</dt><dd className="text-gray-700">{o.rejection_reason}</dd></div>
                        )}
                      </dl>
                    </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {rejecting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejecting(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Reject order</h2>
            <p className="text-sm text-gray-600 mb-3">Refund the charge in the Stripe dashboard separately.</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason (visible internally)" rows={3} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={() => setRejecting(null)} variant="secondary">Cancel</Button>
              <Button onClick={reject} variant="danger" loading={pendingId === rejecting.id}>Reject order</Button>
            </div>
          </div>
        </div>
      )}

      {shipping && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShipping(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 mb-3">Mark as shipped</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Carrier</label>
                <input value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="USPS, UPS, FedEx…" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tracking number *</label>
                <input value={trackingNum} onChange={e => setTrackingNum(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={() => setShipping(null)} variant="secondary">Cancel</Button>
              <Button onClick={ship} loading={pendingId === shipping.id} disabled={!trackingNum.trim()}>Mark shipped</Button>
            </div>
          </div>
        </div>
      )}

      {kitDevice && <ProvisioningKitModal device={kitDevice} onClose={() => setKitDevice(null)} />}
    </div>
  );
};

// ─── Provisioning kit modal ────────────────────────────────────────────────
// Renders the per-bridge install command + RPi Imager preset URL + Brother
// QL-800 label (canvas → PNG download). One-time secrets (install_token,
// activation_code) are only available here because they're only returned
// once at provision time — back-end stores hashes only.

const ProvisioningKitModal: React.FC<{ device: ProvisionedDevice; onClose: () => void }> = ({ device, onClose }) => {
  const urls = bridgeClaimApi.provisioningKitUrls(device.serial, device.install_token);
  const claimUrl = `https://app.intrlock.io/claim-bridge?code=${encodeURIComponent(device.activation_code)}`;
  const [copied, setCopied] = useState<string | null>(null);
  const labelCanvasRef = useRef<HTMLCanvasElement>(null);

  // Render the Brother QL-800 label (DK-22205 continuous tape, 62mm wide).
  // At ~300dpi that's 696px wide; we render 696×600 (~50mm tall) at 2× density
  // for crispness, downscaled visually to a reasonable preview.
  useEffect(() => {
    const canvas = labelCanvasRef.current;
    if (!canvas) return;
    const W = 1392; // 2× 696
    const H = 1200; // 2× 600
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // Brand bar
    ctx.fillStyle = '#0f5132';
    ctx.fillRect(0, 0, W, 100);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 64px Inter, Helvetica, Arial, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText('INTRLOCK BRIDGE', 36, 50);
    ctx.font = '36px Inter, Helvetica, Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(device.role + (device.has_cellular ? ' · cellular' : ''), W - 36, 50);
    ctx.textAlign = 'left';

    // QR code (claim URL) — placed left side
    QRCode.toDataURL(claimUrl, { width: 700, margin: 1, errorCorrectionLevel: 'M' })
      .then(dataUrl => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 80, 160, 700, 700);
          // Right column: human-readable text
          let y = 220;
          ctx.fillStyle = '#111';
          ctx.font = '38px Inter, Helvetica, Arial, sans-serif';
          ctx.fillText('Serial', 850, y); y += 50;
          ctx.font = 'bold 44px "SF Mono", Menlo, monospace';
          ctx.fillText(device.serial, 850, y); y += 90;
          ctx.font = '38px Inter, Helvetica, Arial, sans-serif';
          ctx.fillText('Activation code', 850, y); y += 60;
          ctx.font = 'bold 64px "SF Mono", Menlo, monospace';
          ctx.fillStyle = '#0f5132';
          ctx.fillText(device.activation_code, 850, y); y += 90;
          ctx.font = '32px Inter, Helvetica, Arial, sans-serif';
          ctx.fillStyle = '#555';
          ctx.fillText('Scan QR or enter code at', 850, y); y += 42;
          ctx.fillText('app.intrlock.io/claim-bridge', 850, y);

          // Bottom footer
          ctx.fillStyle = '#888';
          ctx.font = '28px Inter, Helvetica, Arial, sans-serif';
          ctx.fillText(new Date().toISOString().slice(0, 10), 80, H - 50);
          ctx.textAlign = 'right';
          ctx.fillText('intrlock.io', W - 80, H - 50);
          ctx.textAlign = 'left';
        };
        img.src = dataUrl;
      })
      .catch(() => { /* ignore */ });
  }, [device, claimUrl]);

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const downloadLabel = () => {
    const canvas = labelCanvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${device.serial}-label.png`;
    a.click();
  };

  const printLabel = () => {
    const canvas = labelCanvasRef.current;
    if (!canvas) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>${device.serial} label</title>
      <style>@page{size:62mm 50mm;margin:0} body{margin:0} img{width:62mm;height:auto}</style>
      </head><body><img src="${canvas.toDataURL('image/png')}" onload="window.print();setTimeout(window.close,300)"/></body></html>`);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Provisioning kit — {device.serial}</h2>
              <div className="text-sm text-gray-500">
                {device.device_profile_name} · {device.role}{device.has_cellular ? ' · cellular' : ''}
              </div>
            </div>
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>

          {/* Label preview + print/download */}
          <div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Brother QL-800 label (62 mm × 50 mm)</div>
            <div className="bg-gray-100 rounded p-4 flex justify-center">
              <canvas ref={labelCanvasRef} className="bg-white border border-gray-300 shadow-sm" style={{ width: 480, height: 'auto' }} />
            </div>
            <div className="mt-2 flex gap-2 justify-end">
              <Button size="sm" variant="secondary" onClick={downloadLabel}>Download PNG</Button>
              <Button size="sm" onClick={printLabel}>Print label</Button>
            </div>
          </div>

          {/* Operator instructions */}
          <div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-2">1 — Flash microSD with RPi Imager</div>
            <p className="text-sm text-gray-700 mb-2">
              In RPi Imager, click "Edit Settings" → "General" tab. Set the hostname / SSH key from this preset URL:
            </p>
            <div className="flex items-center gap-2 mb-1">
              <code className="flex-1 text-xs bg-gray-100 px-2 py-1.5 rounded font-mono break-all">{urls.preset}</code>
              <button onClick={() => copy(urls.preset, 'preset')} className="text-emerald-700 hover:underline text-xs">{copied === 'preset' ? '✓' : 'copy'}</button>
            </div>
            <p className="text-xs text-gray-500">
              The preset auto-fills hostname <code className="text-gray-700">bridge-{device.serial.toLowerCase()}</code> and the per-bridge SSH public key.
            </p>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-2">2 — Boot, SSH in, run install</div>
            <p className="text-sm text-gray-700 mb-2">
              SSH into the Pi (key from Secrets Manager: <code className="text-xs">{device.ssh_private_key_secret_arn.split(':').pop()}</code>) and paste:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-gray-900 text-emerald-200 px-3 py-2 rounded font-mono break-all">{urls.installCommand}</code>
              <button onClick={() => copy(urls.installCommand, 'cmd')} className="text-emerald-700 hover:underline text-xs">{copied === 'cmd' ? '✓' : 'copy'}</button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              The script downloads the IoT cert (one-shot — install token expires after first use), configures the agent for role <strong>{device.role}</strong>{device.has_cellular ? ', enables ModemManager for the cellular modem' : ''}, and heartbeats home.
            </p>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-600 uppercase mb-2">3 — Customer claim</div>
            <p className="text-sm text-gray-700 mb-2">
              The customer enters the activation code on the label or scans the QR. URL:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-gray-100 px-2 py-1.5 rounded font-mono break-all">{claimUrl}</code>
              <button onClick={() => copy(claimUrl, 'claim')} className="text-emerald-700 hover:underline text-xs">{copied === 'claim' ? '✓' : 'copy'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
